import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR, fmtDateTime, STATUS_ORDER, STATUS_COLORS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ArrowLeft, TrendingUp, Package, Factory, Truck, FileCheck, MapPin, Wallet, ClipboardList, AlertTriangle, Users2 } from "lucide-react";
import DateRangeFilter from "@/components/DateRangeFilter";
import PriorityAlertsPanel from "@/components/PriorityAlertsPanel";
import AnalyticsWidgets from "@/components/AnalyticsWidgets";

/** Symmetric KPI card — fixed height, consistent typography, optional Link target. */
function Stat({ icon: Icon, label, value, sub, tone = "slate", testid, to }) {
  const toneMap = {
    slate:   { bg: "bg-white",        ring: "border-slate-200",    text: "text-slate-900", ic: "text-slate-500" },
    amber:   { bg: "bg-amber-50/60",  ring: "border-amber-200",    text: "text-amber-900", ic: "text-amber-600" },
    blue:    { bg: "bg-blue-50/60",   ring: "border-blue-200",     text: "text-blue-900",  ic: "text-blue-600" },
    emerald: { bg: "bg-emerald-50/60",ring: "border-emerald-200",  text: "text-emerald-900", ic: "text-emerald-600" },
    rose:    { bg: "bg-rose-50/60",   ring: "border-rose-200",     text: "text-rose-900",  ic: "text-rose-600" },
  };
  const t = toneMap[tone] || toneMap.slate;
  const body = (
    <Card className={`h-[118px] p-4 border ${t.ring} ${t.bg} shadow-none rounded-xl flex flex-col justify-between ${to ? "cursor-pointer hover:shadow-sm transition-shadow" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`h-4 w-4 ${t.ic}`} />}
        <div className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">{label}</div>
      </div>
      <div>
        <div className={`heading text-[24px] font-bold tabular-nums leading-none ${t.text}`}>{value}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
      </div>
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

/** Horizontal pipeline — same as before, cleaner spacing. */
function StagePipeline({ byStatus }) {
  const total = Object.values(byStatus || {}).reduce((a, b) => a + (b.count || 0), 0) || 1;
  return (
    <Card className="p-5 border border-slate-200 shadow-none rounded-xl">
      <div className="flex items-center justify-between mb-4">
        <div className="heading font-semibold text-slate-900">Order Pipeline</div>
        <div className="text-xs text-slate-500">{total} total · live</div>
      </div>
      <div className="overflow-x-auto">
        <div className="flex gap-1.5 min-w-max">
          {STATUS_ORDER.map((s, i) => {
            const cnt = byStatus?.[s]?.count || 0;
            const c = STATUS_COLORS[s] || {};
            const pct = Math.max(6, Math.round((cnt / total) * 100));
            return (
              <Link key={s} to={`/orders?status=${encodeURIComponent(s)}`} data-testid={`pipe-stage-${s.toLowerCase().replace(/\s+/g, "-")}`} className="group flex-1 min-w-[104px]" style={{ flex: `${pct} 1 0%` }}>
                <div className="rounded-lg px-3 py-2.5 border transition-all group-hover:-translate-y-0.5 group-hover:shadow-md" style={{ background: c.bg, borderColor: c.border }}>
                  <div className="text-[10px] uppercase tracking-wide opacity-70" style={{ color: c.text }}>{`Stage ${i + 1}`}</div>
                  <div className="heading text-xl font-bold" style={{ color: c.text }}>{cnt}</div>
                  <div className="text-[11px] font-medium truncate" style={{ color: c.text }}>{s}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

const PIE_PALETTE = ["#F59E0B","#10B981","#3B82F6","#EC4899","#8B5CF6","#F97316","#14B8A6","#6366F1","#EF4444","#84CC16","#06B6D4","#A855F7"];

function DrillPie({ title, testid, group, showValue }) {
  const [path, setPath] = useState([]);
  const [rows, setRows] = useState([]);
  const currentGroup = group === "geo" ? (path.length === 0 ? "state" : "district") : "category";
  useEffect(() => {
    const params = { group_by: currentGroup };
    if (currentGroup === "district") params.state = path[0];
    api.get("/dashboard/drilldown", { params }).then((r) => setRows(r.data.rows || []));
  }, [currentGroup, path]);
  const data = rows.map((r, i) => ({ name: r.name || "—", value: showValue ? (r.value || 0) : (r.quantity || r.count || 0), raw: r, fill: PIE_PALETTE[i % PIE_PALETTE.length] }));
  const total = data.reduce((a, b) => a + b.value, 0);
  return (
    <Card className="p-5 border border-slate-200 shadow-none rounded-xl h-[340px] flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="heading font-semibold text-slate-900">{title}</div>
          {path.length > 0 && (
            <button onClick={() => setPath(path.slice(0, -1))} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mt-0.5" data-testid={`${testid}-back`}>
              <ArrowLeft className="h-3 w-3" /> Back to States
            </button>
          )}
        </div>
        <div className="text-[11px] text-slate-500 uppercase tracking-wide">{group === "geo" ? (path.length === 0 ? "By State" : `${path[0]} · By District`) : "By Category"}</div>
      </div>
      {data.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-slate-500">No data yet.</div>
      ) : (
        <div className="flex-1 min-h-[240px]">
          <ResponsiveContainer width="99%" height="100%" minHeight={240} minWidth={0} debounce={50}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}
                   onClick={(d) => { if (group === "geo" && path.length === 0 && d?.name) setPath([d.name]); }} isAnimationActive>
                {data.map((d, i) => <Cell key={i} fill={d.fill} cursor={group === "geo" && path.length === 0 ? "pointer" : "default"} />)}
              </Pie>
              <Tooltip formatter={(v) => (showValue ? formatINR(v) : v.toLocaleString())} />
              <Legend verticalAlign="bottom" height={24} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="text-[11px] text-slate-500 mt-2 text-right">{showValue ? `Total ${formatINR(total)}` : `Total ${total.toLocaleString()} units`}</div>
    </Card>
  );
}

/** Role → ordered list of 4 primary KPIs. Always exactly 4 so the top grid stays symmetric. */
function primaryKPIs(role, s, showValue) {
  const ex = s.extras || {};
  switch (role) {
    case "accounts": return [
      { icon: FileCheck, label: "Approval Queue", value: ex.accounts?.queue_count ?? 0, sub: showValue ? `${formatINR(ex.accounts?.queue_value || 0)} pending` : null, tone: "amber", testid: "kpi-accounts-queue", to: "/orders?status=Submitted" },
      { icon: AlertTriangle, label: "On Hold", value: ex.accounts?.hold_count ?? 0, sub: "awaiting action", tone: "rose", testid: "kpi-accounts-hold", to: "/orders?status=Accounts%20Hold" },
      { icon: ClipboardList, label: "Today's Orders", value: s.todays_orders, sub: "incoming", tone: "blue", testid: "kpi-today" },
      { icon: TrendingUp, label: "Total Orders", value: s.total_orders, sub: showValue ? formatINR(s.total_value) : "", tone: "slate", testid: "kpi-total" },
    ];
    case "production": return [
      { icon: Package, label: "Approved Queue", value: ex.production?.approved_skus ?? 0, sub: "distinct SKUs", tone: "amber", testid: "kpi-prod-skus", to: "/production" },
      { icon: Factory, label: "Net Production", value: Number(ex.production?.net_production ?? 0).toLocaleString(), sub: `${ex.production?.stock_coverage_pct ?? 0}% stock cover`, tone: "emerald", testid: "kpi-prod-net" },
      { icon: ClipboardList, label: "In Production", value: s.by_status?.["In Production"]?.count || 0, sub: "active", tone: "blue", testid: "kpi-prod-active" },
      { icon: TrendingUp, label: "Completed", value: s.by_status?.["Production Completed"]?.count || 0, sub: "ready to dispatch", tone: "slate", testid: "kpi-prod-done" },
    ];
    case "dispatch": return [
      { icon: Truck, label: "Trucks Planned", value: ex.dispatch?.trucks ?? 0, sub: `${ex.dispatch?.orders_planned ?? 0} orders`, tone: "blue", testid: "kpi-disp-trucks", to: "/dispatch" },
      { icon: AlertTriangle, label: "Unassigned", value: ex.dispatch?.unassigned ?? 0, sub: "awaiting truck", tone: "amber", testid: "kpi-disp-unassigned" },
      { icon: Package, label: "Tonnage Planned", value: `${Number(ex.dispatch?.total_weight_kg ?? 0).toLocaleString()}`, sub: "kg", tone: "slate", testid: "kpi-disp-weight" },
      { icon: TrendingUp, label: "Dispatched", value: s.by_status?.Dispatched?.count || 0, sub: "done", tone: "emerald", testid: "kpi-disp-done" },
    ];
    case "sales": return [
      { icon: MapPin, label: "My Territory", value: s.total_orders, sub: "orders in region", tone: "slate", testid: "kpi-sales-total" },
      { icon: ClipboardList, label: "Today's PFIs", value: s.todays_orders, sub: "created today", tone: "blue", testid: "kpi-sales-today" },
      { icon: Wallet, label: "Pipeline Value", value: formatINR(s.total_value), sub: "region total", tone: "emerald", testid: "kpi-sales-value" },
      { icon: TrendingUp, label: "Approval Queue", value: s.pending_accounts, sub: "at Accounts", tone: "amber", testid: "kpi-sales-pending", to: "/orders?status=Submitted" },
    ];
    case "pfi": return [
      { icon: ClipboardList, label: "My PFIs", value: s.total_orders, tone: "slate", testid: "kpi-pfi-total" },
      { icon: TrendingUp, label: "Today", value: s.todays_orders, tone: "blue", testid: "kpi-pfi-today" },
      { icon: FileCheck, label: "At Accounts", value: s.pending_accounts, tone: "amber", testid: "kpi-pfi-pending", to: "/orders?status=Submitted" },
      { icon: Wallet, label: "Total Value", value: formatINR(s.total_value), tone: "emerald", testid: "kpi-pfi-value" },
    ];
    default:  // super_admin, admin, management, operations
      return [
        { icon: ClipboardList, label: "Total Orders", value: s.total_orders, sub: showValue ? formatINR(s.total_value) : "", tone: "slate", testid: "kpi-total" },
        { icon: TrendingUp, label: "Today's Orders", value: s.todays_orders, sub: "incoming", tone: "blue", testid: "kpi-today" },
        { icon: FileCheck, label: "At Accounts", value: s.pending_accounts, sub: "awaiting approval", tone: "amber", testid: "kpi-accounts", to: "/orders?status=Submitted" },
        { icon: Truck, label: "At Dispatch", value: s.pending_dispatch, sub: "planning / ready", tone: "emerald", testid: "kpi-dispatch", to: "/dispatch" },
      ];
  }
}

export default function Dashboard() {
  const { user, cvShow } = useAuth();
  const [s, setS] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  useEffect(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/dashboard/stats", { params }).then((r) => setS(r.data));
  }, [dateFrom, dateTo]);
  if (!s) return <div className="p-8 text-slate-500">Loading dashboard…</div>;
  const role = user?.role;
  // Phase 4: commercial-hide driven by user.effective_permissions.commercial_visibility.show_totals.
  // Legacy role check retained as fallback when permissions haven't loaded.
  const hideValue = !cvShow("show_totals");
  const kpis = primaryKPIs(role, s, !hideValue);

  const showPipeline = role !== "sales" || s.total_orders > 0;
  const showCharts = !hideValue && role !== "pfi" && role !== "sales";
  const showDistributors = role !== "production" && role !== "dispatch";

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="heading text-[26px] font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Live operations overview · {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Signed in as</div>
          <div className="text-sm font-semibold text-slate-900 mt-0.5">{user?.name} <span className="text-[11px] text-slate-500 font-normal">· {role}</span></div>
        </div>
      </div>

      {/* Date-range filter (W-4) */}
      <Card className="p-3 border border-slate-200 shadow-none rounded-xl">
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
          testidPrefix="dashboard-daterange"
        />
        {(dateFrom || dateTo) && (
          <div className="text-[11px] text-slate-500 mt-2" data-testid="dashboard-daterange-summary">
            Filtered {dateFrom ? `from ${dateFrom}` : ""}{dateFrom && dateTo ? " " : ""}{dateTo ? `to ${dateTo}` : ""} · {s.total_orders} order{s.total_orders === 1 ? "" : "s"}
          </div>
        )}
      </Card>

      {/* Priority Alerts (W-3) */}
      <PriorityAlertsPanel canSeeRevenue={!hideValue} />

      {/* Primary KPIs — always exactly 4, symmetric grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => <Stat key={i} {...k} />)}
      </div>

      {/* Pipeline */}
      {showPipeline && <StagePipeline byStatus={s.by_status} />}

      {/* Charts row — only for roles that get value/geo context */}
      {showCharts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <DrillPie title="Geo Distribution" testid="pie-geo" group="geo" showValue={!hideValue} />
          <DrillPie title="Product Categories" testid="pie-category" group="category" showValue={!hideValue} />
        </div>
      )}

      {/* Analytics Widgets (W-3) — revenue widgets respect commercial visibility */}
      <AnalyticsWidgets dateFrom={dateFrom} dateTo={dateTo} canSeeRevenue={!hideValue} />

      {/* Secondary row — Distributors + Recent Orders + Activity (equal columns) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {showDistributors && (
          <Card className="p-5 border border-slate-200 shadow-none rounded-xl h-[380px] flex flex-col" data-testid="card-top-distributors">
            <div className="flex items-center gap-2 mb-4">
              <Users2 className="h-4 w-4 text-slate-500" />
              <div className="heading font-semibold text-slate-900">Top Distributors</div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {s.top_distributors.length === 0 ? <div className="text-sm text-slate-500">No data yet.</div> :
                <div className="space-y-3">
                  {s.top_distributors.map((d, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between text-sm">
                        <div className="truncate pr-2 font-medium text-slate-800">{d.name}</div>
                        {!hideValue ? <div className="font-semibold text-slate-900 tabular-nums">{formatINR(d.value)}</div> : <div className="text-slate-500 text-xs">{d.count} orders</div>}
                      </div>
                      {!hideValue && (
                        <div className="h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
                          <div className="h-full bg-slate-900 rounded-full" style={{ width: `${Math.min(100, (d.value / Math.max(s.top_distributors[0]?.value || 1, 1)) * 100)}%` }} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>}
            </div>
          </Card>
        )}

        <Card className={`p-5 border border-slate-200 shadow-none rounded-xl h-[380px] flex flex-col ${!showDistributors ? "lg:col-span-2" : ""}`} data-testid="card-recent-orders">
          <div className="flex items-center justify-between mb-4">
            <div className="heading font-semibold text-slate-900 flex items-center gap-2"><ClipboardList className="h-4 w-4 text-slate-500" /> Recent Orders</div>
            <Link to="/orders" className="text-xs text-blue-600 hover:underline">View all</Link>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {s.recent_orders.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No orders.</div> :
              s.recent_orders.map((o) => (
                <Link to={`/orders/${o.id}`} key={o.id} className="py-3 flex items-center justify-between hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 mono text-[12px] truncate">{o.pfi_number}</div>
                    <div className="text-[11.5px] text-slate-700 font-semibold truncate">{o.area || "—"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{o.distributor_name}</div>
                  </div>
                  <div className="flex items-center gap-2 text-right shrink-0">
                    {!hideValue && <div className="text-sm font-semibold text-slate-900 tabular-nums">{formatINR(o.grand_total)}</div>}
                    <StatusBadge status={o.status} />
                  </div>
                </Link>
              ))}
          </div>
        </Card>

        <Card className="p-5 border border-slate-200 shadow-none rounded-xl h-[380px] flex flex-col" data-testid="card-recent-activity">
          <div className="flex items-center justify-between mb-4">
            <div className="heading font-semibold text-slate-900">Recent Activity</div>
            <Link to="/activity" className="text-xs text-blue-600 hover:underline">View all</Link>
          </div>
          <ul className="flex-1 overflow-y-auto border-l-2 border-slate-200 pl-4 space-y-4">
            {s.recent_activity.length === 0 ? <div className="text-sm text-slate-500">No activity.</div> :
              s.recent_activity.map((a) => (
                <li key={a.id} className="relative">
                  <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-amber-500 border-2 border-white" />
                  <div className="text-sm text-slate-900"><span className="font-medium">{a.actor_name}</span> <span className="text-slate-500 text-xs">· {a.actor_role}</span> — {a.action.replace(/_/g, " ")} {a.new_status ? <> → <span className="font-medium">{a.new_status}</span></> : null}</div>
                  <div className="text-xs text-slate-500">{fmtDateTime(a.timestamp)}</div>
                </li>
              ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
